import { MultiSelectCombobox, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { Label } from '@/components/shadcn/label'
import { useRestClient } from '@/contexts/QueryProvider'
import { useEffect, useMemo, useRef } from 'react'
import {
  ApplicationAssignment,
  assignmentsFromPeople,
  formatUserOrNominee,
  personKey,
  uniquePeople,
  userLabelFromOptions,
  UserOrNominee,
} from '../../Requests/types'

type ApplicationRoleAssignmentsFieldProps = {
  label: string
  applicationId: number
  people: UserOrNominee[]
  userOptions: SelectOption<number>[]
  value: ApplicationAssignment[]
  onChange: (value: ApplicationAssignment[]) => void
  noApplicationMessage?: string
  noPeopleMessage?: string
}

export function ApplicationRoleAssignmentsField({
  label,
  applicationId,
  people,
  userOptions,
  value,
  onChange,
  noApplicationMessage = 'Select an application to assign roles.',
  noPeopleMessage = 'Add people above to assign their roles.',
}: ApplicationRoleAssignmentsFieldProps) {
  const { getApplicationsApiListApplicationRoles } = useRestClient()

  const { data: roles } = getApplicationsApiListApplicationRoles.useQuery(
    { query: { applicationId } },
    { enabled: applicationId > 0 }
  )

  const roleOptions = useMemo<SelectOption<number>[]>(
    () => roles?.map((role) => ({ value: role.id!, label: role.name ?? `Role ${role.id}` })) ?? [],
    [roles]
  )

  const sourcePeople = useMemo(() => uniquePeople(people), [people])

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const lastApplicationId = useRef(applicationId)

  useEffect(() => {
    // Roles belong to a single application, so switching application drops the previous picks.
    const applicationChanged = lastApplicationId.current !== applicationId
    lastApplicationId.current = applicationId

    const base = applicationChanged ? value.map((assignment) => ({ ...assignment, roleIds: [] })) : value
    const next = assignmentsFromPeople(sourcePeople, base)

    if (JSON.stringify(next) === JSON.stringify(value)) return
    onChangeRef.current(next)
  }, [applicationId, sourcePeople, value])

  const updateRoleIds = (index: number, roleIds: number[]) => {
    onChange(value.map((assignment, i) => (i === index ? { ...assignment, roleIds } : assignment)))
  }

  return (
    <div className="space-y-3">
      <Label>{label} *</Label>
      {applicationId <= 0 && <p className="text-sm text-muted-foreground">{noApplicationMessage}</p>}
      {applicationId > 0 && value.length === 0 && (
        <p className="text-sm text-muted-foreground">{noPeopleMessage}</p>
      )}
      {applicationId > 0 &&
        value.map((assignment, index) => (
          <div
            key={`assignment-${personKey(assignment) ?? index}`}
            className="space-y-2 rounded-md border p-3"
          >
            <div className="text-sm font-medium">
              {formatUserOrNominee(assignment, userLabelFromOptions(assignment.userId, userOptions))}
            </div>
            <MultiSelectCombobox
              options={roleOptions}
              selectedValues={assignment.roleIds}
              onSelectionChange={(roleIds) => updateRoleIds(index, roleIds)}
              placeholder="Select roles..."
              searchPlaceholder="Search roles..."
              renderBadgesInTrigger
            />
          </div>
        ))}
    </div>
  )
}
